import { FileText, User, Settings, Clock, Play, CheckCircle2, XCircle, RefreshCw, Search, AlertTriangle, Package, HelpCircle, Link, Layers } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatDateTime } from '@/lib/utils';
import type { AuditLogEntry, AuditEventType } from '@/types';

interface AuditLogDetailProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: AuditLogEntry | null;
}

const eventTypeConfig: Record<AuditEventType, {
  label: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
  icon: typeof FileText;
}> = {
  MigrationStarted: { label: 'Migration Started', variant: 'default', icon: Play },
  MigrationCompleted: { label: 'Migration Complete', variant: 'default', icon: CheckCircle2 },
  MigrationFailed: { label: 'Migration Failed', variant: 'destructive', icon: XCircle },
  MigrationCancelled: { label: 'Migration Cancelled', variant: 'outline', icon: XCircle },
  MigrationRetried: { label: 'Migration Retried', variant: 'secondary', icon: RefreshCw },
  ModuleStarted: { label: 'Module Started', variant: 'default', icon: Package },
  ModuleCompleted: { label: 'Module Complete', variant: 'default', icon: CheckCircle2 },
  ModuleFailed: { label: 'Module Failed', variant: 'destructive', icon: XCircle },
  ModuleRetried: { label: 'Module Retried', variant: 'secondary', icon: RefreshCw },
  ConfigurationChanged: { label: 'Config Changed', variant: 'secondary', icon: Settings },
  UserLoggedIn: { label: 'Login', variant: 'default', icon: User },
  UserLoggedOut: { label: 'Logout', variant: 'outline', icon: User },
  BridgeLookupPerformed: { label: 'Bridge Lookup', variant: 'secondary', icon: Search },
  BridgeDuplicateDetected: { label: 'Bridge Duplicate', variant: 'destructive', icon: AlertTriangle },
  MigrationOrganizationAttached: { label: 'Organization Attached', variant: 'secondary', icon: Link },
};

const fallbackConfig = {
  label: 'Unknown',
  variant: 'outline' as const,
  icon: HelpCircle,
};

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(formatDetailValue).join(', ');
  return JSON.stringify(value);
}

function formatDetailKey(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

export function AuditLogDetail({
  open,
  onOpenChange,
  entry,
}: AuditLogDetailProps) {
  if (!entry) return null;

  const config = eventTypeConfig[entry.eventType] ?? fallbackConfig;
  const Icon = config.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            Audit Log Entry
          </DialogTitle>
          <DialogDescription>
            Full details of the audit event
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Event Type */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Event Type</span>
            <Badge variant={config.variant} className="gap-1">
              <Icon className="h-3 w-3" />
              {config.label}
            </Badge>
          </div>

          <Separator />

          {/* Timestamp */}
          <div className="flex items-start gap-3">
            <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">Timestamp</p>
              <p className="text-sm text-muted-foreground">{formatDateTime(entry.timestamp)}</p>
            </div>
          </div>

          {/* User */}
          <div className="flex items-start gap-3">
            <User className="h-4 w-4 text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">User</p>
              <p className="text-sm text-muted-foreground">{entry.userName ?? 'System'}</p>
              {entry.userId && (
                <p className="text-xs text-muted-foreground">ID: {entry.userId}</p>
              )}
            </div>
          </div>

          {/* Migration ID */}
          {entry.migrationId && (
            <div className="flex items-start gap-3">
              <Link className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Migration ID</p>
                <p className="text-xs text-muted-foreground font-mono">{entry.migrationId}</p>
              </div>
            </div>
          )}

          {/* Module Key */}
          {entry.moduleKey && (
            <div className="flex items-start gap-3">
              <Layers className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Module</p>
                <p className="text-sm text-muted-foreground">{entry.moduleKey}</p>
              </div>
            </div>
          )}

          <Separator />

          {/* Description */}
          <div>
            <p className="text-sm font-medium mb-1">Description</p>
            <p className="text-sm text-muted-foreground">{entry.description}</p>
          </div>

          {/* Details */}
          {entry.details && Object.keys(entry.details).length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Additional Details</p>
              <dl className="bg-muted/30 rounded-lg p-3 space-y-2">
                {Object.entries(entry.details).map(([key, value]) => (
                  <div key={key} className="flex justify-between gap-4">
                    <dt className="text-xs text-muted-foreground shrink-0">{formatDetailKey(key)}</dt>
                    <dd className="text-xs font-mono text-right break-all">{formatDetailValue(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
